import type { InitializeMembershipRemoval } from '@bliks-io/contracts';
import type { MembershipRowInput, ParsedMembershipRow, ParsedOrgFunctionRow } from '@/lib/excel';
import type { RowDeleteAction } from '../components/EditableDraftTable';
import { serializeSurfaceRow } from './flushBaselines';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';

/**
 * Delete/clear policy and removal bookkeeping of the Funktionen/Teams surface
 * (ADR 0010). Persisted rows are deletable — the import endpoint accepts
 * `removals` — EXCEPT a Funktion that is some member's Hauptfunktion (Stelle):
 * deleting it would orphan that person's position, so it stays locked here
 * and is managed via the Mitarbeiter step / organization settings.
 */

export function isMainRoleLockedRow(
  row: { entityId?: string },
  mainRoleFunctionIds: ReadonlySet<string>
): boolean {
  return row.entityId !== undefined && mainRoleFunctionIds.has(row.entityId);
}

export function orgFunctionDeleteAction(
  row: { entityId?: string },
  mainRoleFunctionIds: ReadonlySet<string>
): RowDeleteAction {
  return isMainRoleLockedRow(row, mainRoleFunctionIds) ? 'disabled' : 'enabled';
}

/**
 * Persisted entity ids present in the previous table but gone from the next —
 * queued as pending removals the Weiter submit sends. Runs on every
 * onRowsChange: edits keep their id and diff to nothing.
 */
export function collectRemovedEntityIds(
  previousRows: readonly ParsedOrgFunctionRow[],
  nextRows: readonly ParsedOrgFunctionRow[]
): string[] {
  const nextIds = new Set(nextRows.map((row) => row.entityId).filter(Boolean));
  return previousRows
    .map((row) => row.entityId)
    .filter((id): id is string => id !== undefined && !nextIds.has(id));
}

/**
 * Flushed membership rows (already persisted by a silent flush) present in
 * the previous table but gone from the next — queued as pending
 * membershipRemovals. Editing a flushed row also queues its OLD pair: the
 * resend-all flush links the new pair, the removal unlinks the stale one.
 * Never-flushed rows only exist client-side and diff to nothing.
 */
export function collectRemovedFlushedMemberships(
  previousRows: readonly ParsedMembershipRow[],
  nextRows: readonly ParsedMembershipRow[],
  flushedMembershipKeys: ReadonlySet<string>
): MembershipRowInput[] {
  const nextKeys = new Set(nextRows.map(serializeSurfaceRow));
  return previousRows
    .filter((row) => {
      const key = serializeSurfaceRow(row);
      return flushedMembershipKeys.has(key) && !nextKeys.has(key);
    })
    .map((row) => row.data);
}

/**
 * Name → OrgFunction id, case-insensitive, for resolving membership rows to
 * their link target. Current table rows win over the server copies: after an
 * in-place rename the row carries the latest name for the same id.
 */
export function buildOrgFunctionIdLookup(
  rows: readonly ParsedOrgFunctionRow[],
  existingOrgFunctions: readonly { _id: string; name: string }[]
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const orgFunction of existingOrgFunctions) {
    lookup.set(normalizedKey(orgFunction.name), orgFunction._id);
  }
  for (const row of rows) {
    if (row.entityId) lookup.set(normalizedKey(row.data.name), row.entityId);
  }
  return lookup;
}

/**
 * Membership unlinks go on the wire keyed by OrgFunction id — names rename
 * and resolve case-insensitively, so a name key could miss or hit the wrong
 * entity (see InitializeMembershipRemovalSchema). Resolution happens at queue
 * time, while table and server names still agree. A pair whose target
 * resolves to nothing is dropped: its function is gone or queued for removal,
 * and the entity removal already pulls every secondaryRoles link.
 */
export function resolveMembershipRemovals(
  removedRows: readonly MembershipRowInput[],
  idByFunctionName: ReadonlyMap<string, string>
): InitializeMembershipRemoval[] {
  return removedRows.flatMap((data) => {
    const orgFunctionId = idByFunctionName.get(normalizedKey(data.orgFunctionName));
    return orgFunctionId ? [{ orgFunctionId, memberEmail: data.memberEmail }] : [];
  });
}
